import { logMessage } from './sentry';

const hexRegex = /^#(?:[0-9a-f]{3}){1,2}$/i;

export function hexToRGB(hex: string): string;
export function hexToRGB(hex: string, outputArray: boolean): number[];
export function hexToRGB(
  hex: string,
  outputArray: boolean = false
): string | number[] {
  hex = hex.trim();

  if (!hexRegex.test(hex)) {
    logMessage(`"${hex}" is not a valid hex color`);
    return '#ffffff';
  }

  let r: number;
  let g: number;
  let b: number;
  hex = hex.substring(1);
  if (hex.length === 3) {
    // shorthand hex color ex: #FFF
    [r, g, b] = [
      parseInt(`${hex[0]}${hex[0]}`, 16),
      parseInt(`${hex[1]}${hex[1]}`, 16),
      parseInt(`${hex[2]}${hex[2]}`, 16)
    ];
  } else {
    // longhand hex color ex: #FFFFFF
    [r, g, b] = [
      parseInt(`${hex[0]}${hex[1]}`, 16),
      parseInt(`${hex[2]}${hex[3]}`, 16),
      parseInt(`${hex[4]}${hex[5]}`, 16)
    ];
  }

  if (outputArray) {
    return [r, g, b];
  }

  return `rgb(${r},${g},${b})`;
}

// 0 < alpha < 1
export function hexToRGBA(hex: string, alpha: number): string;
export function hexToRGBA(
  hex: string,
  alpha: number,
  outputArray: boolean
): number[];
export function hexToRGBA(
  hex: string,
  alpha: number = 1,
  outputArray: boolean = false
): string | number[] {
  hex = hex.trim();
  alpha = Math.min(Math.max(0, alpha), 1);

  if (!hexRegex.test(hex)) {
    logMessage(`"${hex}" is not a valid hex color`);
    return '#ffffff';
  }

  const [r, g, b] = hexToRGB(hex, true) as number[];

  if (outputArray) {
    return [r, g, b, alpha];
  }

  return `rgba(${r},${g},${b},${alpha})`;
}

export function isHex(hex: string) {
  return hexRegex.test(hex);
}

export function hexTo6digits(hex: string) {
  if (!hexRegex.test(hex) || hex.length !== 4) {
    return hex;
  }

  const [, r, g, b] = hex.split('');

  return `#${r}${r}${g}${g}${b}${b}`;
}

export function rgbaToHex(rgba: string): string {
  const inParts = rgba.substring(rgba.indexOf('(')).split(',');
  const r = parseInt(inParts[0].substring(1).trim(), 10);
  const g = parseInt(inParts[1].trim(), 10);
  const b = parseInt(inParts[2].trim(), 10);
  const a = +parseFloat(
    inParts[3].substring(0, inParts[3].length - 1).trim()
  ).toFixed(2);
  const outParts = [
    Math.round(r * a).toString(16),
    Math.round(g * a).toString(16),
    Math.round(b * a).toString(16)
  ];

  // Pad single-digit output values
  outParts.forEach(function(part, i) {
    if (part.length === 1) {
      outParts[i] = '0' + part;
    }
  });

  return '#' + outParts.join('');
}

export function rgbToHex(rgb: string): string {
  const inParts = rgb.substring(rgb.indexOf('(')).split(',');
  const r = parseInt(inParts[0].substring(1).trim(), 10);
  const g = parseInt(inParts[1].trim(), 10);
  const b = parseInt(inParts[2].trim(), 10);
  const outParts = [r.toString(16), g.toString(16), b.toString(16)];

  // Pad single-digit output values
  outParts.forEach(function(part, i) {
    if (part.length === 1) {
      outParts[i] = '0' + part;
    }
  });

  return '#' + outParts.join('');
}

export function parseColor(color: string): string {
  color = color.trim();

  if (color.indexOf('rgb(') === 0) {
    return rgbToHex(color);
  } else if (color.indexOf('rgba(') === 0) {
    return rgbaToHex(color);
  } else if (isHex(color) && color.length === 4) {
    return hexTo6digits(color);
  } else if (!isHex(color) || color.length !== 7) {
    console.warn('color string value is not supported', color);
  }

  return color;
}

export function shadeColor(color: string, percent: number): string {
  const colorParsed = parseColor(color);

  if (!isHex(colorParsed) || colorParsed.length !== 7) {
    return colorParsed;
  }

  const rgb = [
    parseInt(colorParsed.substring(1, 3), 16),
    parseInt(colorParsed.substring(3, 5), 16),
    parseInt(colorParsed.substring(5, 7), 16)
  ];

  const hexValue = rgb
    .map(value => {
      if (percent >= 0) {
        const diff = 255 - value;
        value += Math.round((diff * percent) / 100);
      } else {
        value = Math.round((value * (100 + percent)) / 100);
      }
      value = value < 255 ? value : 255;
      value = value > 0 ? value : 0;
      const hexStr = value.toString(16);
      return hexStr.length === 1 ? '0' + hexStr : hexStr;
    })
    .join('');

  return '#' + hexValue;
}

export function getColorLuminance(hexadecimalColor: string): number {
  return (
    (0.299 * parseInt(hexadecimalColor.slice(1, 3), 16) +
      0.587 * parseInt(hexadecimalColor.slice(3, 5), 16) +
      0.114 * parseInt(hexadecimalColor.slice(5, 7), 16)) /
    255
  );
}

export function isColorLight(hexadecimalColor: string): boolean {
  return getColorLuminance(hexadecimalColor) > 0.5;
}
